<template>
  <div class="module-hub-content">
    <div class="module-hub-container">
      <div class="module-body" v-if="isDesignMode">
        <div class="add-new-module">
          <div class="module-header">
            <h2 class="module-header-title">Add New Module</h2>
          </div>
          <div class="module-items default">
            <div class="module-item" :class="workspaceLoading || deletingModule || moduleNameEditing || isCreatingModule
              ? 'disable'
              : ''
              " @click="createModule" data-testid="module-hub-create-module">
              <div class="module-item-header">
                <span class="module-item-title">⚙️</span>
              </div>
              <div class="module-item-body">
                <p class="module-item-sub">Create</p>
                <p class="module-item-description">
                  Build from scratch with full flexibility.
                </p>
              </div>
            </div>
            <div class="module-item" :class="workspaceLoading || deletingModule || moduleNameEditing ? 'disable' : ''"
              @click="openAIIntegrationModal">
              <div class="module-item-header">
                <div class="module-item-title-icon-row">
                  <img class="module-item-title-icon" src="../../assets/images/ai/claude.svg" alt="ai-teammate">
                  <img class="module-item-title-icon" src="../../assets/images/ai/chatgpt.svg" alt="ai-teammate">
                </div>
              </div>
              <div class="module-item-body">
                <p class="module-item-sub">Connect to AI</p>
                <p class="module-item-description">
                  Easily integrate AI into your module for smarter functionality.
                </p>
              </div>
            </div>
            <div class="module-item disable" :class="workspaceLoading || deletingModule || moduleNameEditing
              ? 'disable'
              : ''
              ">
              <div class="module-item-header">
                <img class="module-item-title-icon" src="../../assets/images/ai/ai-sparkle.svg" alt="ai-teammate">
                <span class="module-item-soon">COMING SOON</span>
              </div>
              <div class="module-item-body">
                <p class="module-item-sub">AI Teammate</p>
                <p class="module-item-description">
                  Choose a similar template, use or make improvements from
                  there.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="list-modules">
          <div class="list-header">
            <h2 class="list-title">
              List of Modules
              <span class="list-modules-count" data-testid="module-hub-module-count">{{ filteringWorkspace ?
                filteringWorkspace.length : 0 }}</span>
            </h2>
          </div>

          <div class="list-items-empty" v-if="!workspaceLoading && !workspaceModules.length" @click="createModule">
            <img :src="emptyRecordImage" class="list-items-empty-img" />
            <div class="list-items-empty__title">Nothing here</div>
            <div class="list-items-empty__description">
              <div>It looks like you haven't created any modules yet.</div>
              <div>Get started by creating your first module.</div>
            </div>
          </div>
          <div class="list-modules-inner" v-else>
            <div class="list-search">
              <label for="search_input">
                <input class="fts-form-control" type="text" placeholder="Search" id="search_input"
                  v-model="search_input" data-testid="module-hub-search" />
                <template v-if="search_input">
                  <span @click="resetSearchInput" class="material-icons-outlined close-btn">
                    close
                  </span>
                </template>
                <template v-else>
                  <span class="material-icons-outlined">search</span>
                </template>
              </label>
            </div>
            <div class="list-items">
              <div class="list-item" v-for="(module, index) in filteringWorkspace" :key="module.id"
                :data-testid="`module-hub-list-item-${module.id}`" @click="pushToRoute(module.id)">
                <div class="list-item-icon" @click.stop>
                  <fts-emoji :appendToBody="true" :toggleSize="28" v-model="module.emoji"
                    class="console-card-module-emoji" @input="queueModuleSave(module.id)" />
                </div>
                <div class="list-item-content" @click.stop>
                  <fts-contenteditable :search="true" v-model.trim="module.name" placeholder="Name this module"
                    class="list-item-title" :data-testid="`module-hub-module-name-${module.id}`"
                    @input="validateModuleName(index)" />

                  <div class="list-item-title-error" v-if="isDesignModuleNameError(index)">
                    {{ isDesignModuleNameError(index) }}
                  </div>
                </div>
                <div class="list-item-action">
                  <div class="list-item-delete" :data-testid="`module-hub-delete-${module.id}`"
                    @click.stop="openModal(module.id)">
                    <font-awesome-icon size="lg" width="20" height="20" class="trash-alt"
                      :icon="['far', 'trash-alt']" />
                  </div>
                  <font-awesome-icon size="lg" width="20" height="20" class="agnle-right"
                    :icon="['fas', 'angle-right']" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <fts-confirmation ref="confirmation"> </fts-confirmation>
      </div>
    </div>
    <fts-spinner full-screen boundary="parent"
      v-if="workspaceLoading || deletingModule || moduleNameEditing || isCreatingModule" />

    <!-- AI Integration Modal -->
    <fts-modal-lite ref="aiIntegrationModal" width="886px" append-header-class="ai-integration-modal__header"
      append-content-class="ai-integration-modal__content" append-footer-class="ai-integration-modal__footer"
      class="ai-integration-modal">
      <template #title>
        <div class="ai-integration-modal__title">✨ Choose Your AI Integration</div>
      </template>
      <template #description>
        <div class="ai-integration-modal__desc">Select an AI provider to get started with the integration tutorial.
        </div>
      </template>

      <template #body>
        <div class="ai-integration-body">
          <!-- Connect to ChatGPT -->
          <div class="ai-gpt-connector">
            <div class="ai-gpt-connector__bg">
              <img src="../../assets/images/ai-integration/chatgpt-connector-bg.webp" alt="" />
            </div>
            <img src="../../assets/images/ai/chatgpt.svg" alt="ChatGPT" class="ai-gpt-connector__logo" />
            <div class="ai-gpt-connector__info">
              <div class="ai-gpt-connector__title">Connect to ChatGPT</div>
              <div class="ai-gpt-connector__desc">Connect your ChatGPT account to start using AI capabilities in Inistate.</div>
              <div class="ai-gpt-connector__features">
                <span class="ai-gpt-connector__feature">
                  <font-awesome-icon :icon="['far', 'circle-check']" class="ai-gpt-connector__feature-icon" />
                  Secure connection
                </span>
                <span class="ai-gpt-connector__feature">
                  <font-awesome-icon :icon="['far', 'circle-check']" class="ai-gpt-connector__feature-icon" />
                  Easy setup
                </span>
                <span class="ai-gpt-connector__feature">
                  <font-awesome-icon :icon="['far', 'circle-check']" class="ai-gpt-connector__feature-icon" />
                  Step-by-step guide
                </span>
              </div>
            </div>
            <fts-btn-lite tag="a" href="https://chatgpt.com/plugins/plugin_asdk_app_69f9ad1e0abc819193c9f1bbfe8ed040?q=ini" target="_blank" rel="noopener noreferrer" type="outlined" class="ai-gpt-connector__btn">
              <template #icon-left><font-awesome-icon :icon="['fas', 'link']" /></template>
              Connect
            </fts-btn-lite>
          </div>

          <!-- AI Integration Tutorials accordion -->
          <fts-accordion title="AI Integration Tutorials" :defaultOpen="false">
            <template #header-extra>
              <div class="ai-tutorials-provider-icons">
                <img src="../../assets/images/ai/claude.svg" alt="Claude" class="ai-tutorials-provider-icon" />
                <img src="../../assets/images/ai/chatgpt.svg" alt="ChatGPT" class="ai-tutorials-provider-icon" />
              </div>
            </template>

            <div class="ai-tutorials-body">
              <div class="ai-integration__api-key">
                <div class="ai-profile-form-label ai-integration-label">API Key</div>
                <div class="ai-integration-warning">
                  Warning: Do not pass this key to any untrusted party. Generating new api
                  key will void existing keys, existing integrations have to be replaced
                  with new key.
                </div>

                <div class="ai-integration-api-key-wrapper" v-if="formData.apiKey">
                  <div class="ai-integration-api-key">
                    {{ formData.apiKey }}
                    <div class="ai-integration-copy-hint" v-if="linkCopied">Copied</div>
                  </div>
                  <fts-btn-lite type="text" :floating="true" v-clipboard:copy="formData.apiKey" v-clipboard:success="clipboardOnCopy"
                    v-clipboard:error="clipboardOnError" class="ai-integration-copy-btn">
                    <font-awesome-icon :icon="['far', 'clone']" />
                  </fts-btn-lite>
                </div>

                <div class="ai-integration-generate-action">
                  <fts-btn-lite id="ai-integration-generate-btn" type="outlined" :loading="apiKeyLoading"
                    @click="getAPIKey">
                    <template #icon-left>
                      <font-awesome-icon :icon="['fas', 'rotate']" fixed-width size="lg" class="sidenav-icon" />
                    </template>
                    Generate API Key
                  </fts-btn-lite>
                </div>
              </div>

              <div class="ai-integration-cards">
                <a class="ai-integration-card" href="https://community.inistate.com/t/inistate-ai-integration-claude/1110" target="_blank" rel="noopener noreferrer">
                  <div class="ai-integration-card__icon">
                    <img src="../../assets/images/ai/claude.svg" alt="Claude" />
                  </div>
                  <div class="ai-integration-card__name">Inistate AI Integration - Claude</div>
                  <div class="ai-integration-card__desc">Learn how to connect Claude AI to your module with guided setup instructions and examples.</div>
                  <div class="ai-integration-card__link">View Tutorial <span class="ai-integration-card__link_icon"><img src="../../assets/images/icons/arrow-up-right.svg" alt=""></span></div>
                </a>

                <a class="ai-integration-card" href="https://community.inistate.com/t/inistate-ai-integration-chatgpt/1112" target="_blank" rel="noopener noreferrer">
                  <div class="ai-integration-card__icon">
                    <img src="../../assets/images/ai/chatgpt.svg" alt="ChatGPT" />
                  </div>
                  <div class="ai-integration-card__name">Inistate AI Integration - ChatGPT</div>
                  <div class="ai-integration-card__desc">Learn how to integrate ChatGPT into your module with a simple step-by-step tutorial.</div>
                  <div class="ai-integration-card__link">View Tutorial <span class="ai-integration-card__link_icon"><img src="../../assets/images/icons/arrow-up-right.svg" alt=""></span></div>
                </a>
              </div>

              <div class="ai-integration-tip">
                <span class="ai-integration-tip__accent">💡 Tip:</span> Each tutorial will guide you through the complete integration process, from setup to implementation.
              </div>
            </div>
          </fts-accordion>
        </div>
      </template>
    </fts-modal-lite>
  </div>
</template>

<script>
import lodash from "lodash";
import moment from "moment";
import FTSBtnLite from "../elements/FTSBtnLite";
import FTSEmoji from "../elements/FTSEmoji";
import FTSModalLite from "./FTSModalLite.vue";
import commons from "../commons/commons";
import FTSConfirmation from "../elements/confirmation/FTSConfirmation.vue";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSSpinner from "../elements/FTSSpinner";
import FTSAccordion from "../elements/FTSAccordion.vue";
import { clientStore } from "../store/clientStore";
import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from "../store/canvasDesignStore";
import designValidator from "../commons/designValidator";
import { emptyRecordImage } from "../commons/images";

const _ = lodash;
export default {
  name: "FTSModuleHub",
  data() {
    return {
      linkCopied: false,
      apiKey: null,
      formData: {},
      originalData: {},
      apiKeyLoading: false,
      search_input: "", // list of modules searching
      deleteModuleModal: {
        module: null
      },
      pendingModuleSaves: {},
      moduleNameEditing: null,
      workspaceLoading: false,
      deletingModule: false,
      isCreatingModule: false,
      emptyRecordImage
    };
  },
  validations() {
    return {
      filteringWorkspace: {
        $each: {
          ...designValidator.moduleDesign(this.filteringWorkspace)
        }
      }
    };
  },
  components: {
    "fts-btn-lite": FTSBtnLite,
    "fts-emoji": FTSEmoji,
    "fts-modal-lite": FTSModalLite,
    "fts-confirmation": FTSConfirmation,
    "fts-contenteditable": FTSContenteditable,
    "fts-spinner": FTSSpinner,
    "fts-accordion": FTSAccordion
  },
  methods: {
    async syncWorkspaceStore() {
      const workspaceId = clientStore.getters.workspaceId;

      if (!workspaceId) return;

      try {
        this.workspaceLoading = true;
        await canvasDesignStore.dispatch("loadWorkspace", { id: workspaceId });
        await clientStore.dispatch("loadWorkspace", { id: workspaceId });
      } catch (error) {
        console.error("Failed to sync module hub workspace store:", error);
      } finally {
        this.workspaceLoading = false;
      }
    },
    /* clipboard */
    clipboardOnCopy() {
      if (!this.linkCopied) {
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      }
    },
    clipboardOnError() {
      console.error("ClipboardOnError");
    },
    async getAPIKey() {
      this.apiKeyLoading = true;
      try {
        const res = await authorizationStore.dispatch("getAPIKey");
        authorizationStore.dispatch("setAPIKey", res.data);
        this.formData.apiKey = res.data;
        this.originalData.apiKey = _.cloneDeep(this.formData.apiKey);
      } catch(err) {
        console.error(err);
      } finally {
        this.apiKeyLoading = false;
      }
    },
    resetSearchInput() {
      this.search_input = "";
    },
    queueModuleSave(moduleId) {
      if (moduleId == null) return;

      this.$set(this.pendingModuleSaves, moduleId, true);
      this.debounceSaveModule(moduleId);
    },
    debounceSaveModule: _.debounce(async function (moduleId) {
      const filteredIndex = this.filteringWorkspace.findIndex(
        module => module.id == moduleId
      );
      const module = this.workspaceModules.find(item => item.id == moduleId);

      if (filteredIndex !== -1 && this.isDesignModuleNameError(filteredIndex)) {
        this.$delete(this.pendingModuleSaves, moduleId);
        return;
      }

      if (!module) {
        this.$delete(this.pendingModuleSaves, moduleId);
        return;
      }

      try {
        this.moduleNameEditing = true;
        await canvasDesignStore.dispatch("loadVector", module.id);
        const vector = canvasDesignStore.getters.vector;
        const update = { ...vector, name: module.name, emoji: module.emoji };
        canvasDesignStore.commit("commitVector", update);
        const res = await canvasDesignStore.dispatch("saveVector", update);
        console.info("designExecuteSave:Completed", res);

        await this.syncWorkspaceStore();
        window.eventBus.$emit("listing:schemaUpdated");
      } catch (error) {
        // in case of error, try to sync workspace store to reflect the latest state
        await this.syncWorkspaceStore();
        console.error("Auto-save failed:", error);
      } finally {
        this.$delete(this.pendingModuleSaves, moduleId);
        this.moduleNameEditing = false;
      }
    }, 0),

    async validateModuleName(index) {
      if (
        !this.$v ||
        !this.$v.filteringWorkspace ||
        !this.$v.filteringWorkspace.$each
      )
        return;

      const target = this.$v.filteringWorkspace.$each[index];

      if (!target) return;

      target.name.$touch();

      if (this.isDesignModuleNameError(index)) return;

      const module = this.filteringWorkspace[index];

      if (!module) return;

      await this.designSetModuleEmoji(index);
    },
    designSetModuleEmoji: _.debounce(async function (index) {
      const target = this.filteringWorkspace[index];

      if (!target) return;

      if (target.name) {
        try {
          this.moduleNameEditing = true;
          const res = await canvasDesignStore.dispatch("ai", {
            type: "emoji",
            text: target.name
          });

          if (res.data) {
            target.emoji = res.data;
          }
        } catch (err) {
          console.error(err);
        } finally {
          this.moduleNameEditing = false;
        }
      }

      if (!this.pendingModuleSaves[target.id]) {
        this.queueModuleSave(target.id);
      }
    }, 800),
    isDesignModuleNameError(index) {
      if (
        !this.$v ||
        !this.$v.filteringWorkspace ||
        !this.$v.filteringWorkspace.$each
      )
        return null;

      const target = this.$v.filteringWorkspace.$each[index];
      return designValidator.moduleDesignError(target, "name");
    },
    async openModal(moduleId) {
      const res = await this.$refs.confirmation.show({
        isLite: true,
        type: "delete",
        title: "Delete Module",
        text: "This action cannot be undone."
      });
      if (res === true) {
        this.deleteModuleModal.module = this.workspaceModules.find(
          module => module.id == moduleId
        );
        this.deleteModule();
      }
    },
    getNextUntitledModuleName() {
      const normalizedNames = new Set(
        (this.workspaceModules || [])
          .map(module => module && module.name)
          .filter(Boolean)
          .map(name => name.toString().toLowerCase().trim())
      );
      const baseName = "Untitled";

      if (!normalizedNames.has(baseName.toLowerCase())) return baseName;

      for (let index = 2; index < 50; index += 1) {
        const name = `${baseName} ${index}`;
        if (!normalizedNames.has(name.toLowerCase())) return name;
      }

      return `${baseName} ${moment().format("YYYYMMDDHHmmss")}`;
    },
    initializeCreateModuleCanvas(name) {
      canvasDesignStore.dispatch("resetVector");
      canvasDesignStore.dispatch("initialVector");
      canvasDesignStore.dispatch("initialCanvas");
      canvasDesignStore.dispatch("updateVectorCanvas", {
        ...canvasDesignStore.state.canvas,
        standardActivities: commons.standards
      });
      canvasDesignStore.dispatch("updateModule", {
        ...canvasDesignStore.getters.vector,
        name
      });
    },
    async createModule() {
      if (
        this.workspaceLoading ||
        this.deletingModule ||
        this.moduleNameEditing ||
        this.isCreatingModule
      ) {
        return;
      }

      try {
        this.isCreatingModule = true;
        const moduleName = this.getNextUntitledModuleName();
        this.initializeCreateModuleCanvas(moduleName);

        const res = await canvasDesignStore.dispatch("saveVector");
        const createdModuleId = res && res.data && res.data.id;

        if (!createdModuleId) {
          throw new Error("Module was created without an id.");
        }

        await canvasDesignStore.dispatch("loadVector", createdModuleId);
        await this.syncWorkspaceStore();
        this.pushToRoute(createdModuleId);
      } catch (error) {
        console.error("Failed to create module:", error);
        this.$toasted.global.error({
          response: error.response,
          message: "Unable to create module"
        });
      } finally {
        this.isCreatingModule = false;
      }
    },
    async deleteModule() {
      try {
        const module = this.deleteModuleModal.module;
        const { name, emoji, id } = module;
        if (id == null) {
          this.$toasted.error("Missing Module Id");
          return;
        }
        this.deletingModule = true;
        await canvasDesignStore.dispatch("deleteVector", id);

        await this.syncWorkspaceStore();

        const clientModuleId = clientStore.getters.moduleId;
        if (clientModuleId == id) {
          clientStore.dispatch("resetModule");
        }

        // wait all actions to be completed before showing toast and navigating
        this.$toasted.success(`${emoji ? emoji : ""} ${name} deleted.`);
        this.deletingModule = false;
      } catch (error) {
        console.error(error);
        // in case of error, try to sync workspace store to reflect the latest state
        await this.syncWorkspaceStore();
        this.$toasted.error("Failed to Delete Item");
      } finally {
        this.deletingModule = false;
      }
    },
    openAIIntegrationModal() {
      this.formData = _.cloneDeep(this.profile);
      this.$refs.aiIntegrationModal.show();
    },
    pushToRoute(moduleId) {
      const route = { name: "lite-studio" };
      route.params = { moduleId: commons.encodeids(moduleId) };
      this.$router.push(route);
    }
  },
  computed: {
    filteringWorkspace() {
      const search_input = this.search_input.trim();
      const workspaceModules =
        this.workspaceModules &&
        this.workspaceModules.filter(module => {
          return module?.name
            ?.toLowerCase()
            .includes(search_input.toLowerCase());
        });
      return workspaceModules;
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules || [];
    },
    isDesignMode() {
      if (!clientStore.getters.liteDesignMode) {
        this.$router.push({ name: "lite" });
      }
      return clientStore.getters.liteDesignMode;
    },
    profile() {
      return authorizationStore.getters.userProfile;
    },
  },
  created() {
    this.syncWorkspaceStore();
  },
  mounted() { },
  beforeDestroy() { }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

/* ==========================================================================
   Calm density — P1 (2026-09-05, round 4): docs/frontend-lite-pages-calm-density-proposal.md,
   rulings H1–H4 (recommended options). Class-level only: the "Add New Module" and "List of
   Modules" headings, every module-hub-* testid and the AI-integration modal are untouched.
   Placed FIRST in the block so the original rules below stay readable; specificity carries it
   (every selector is nested under .module-hub-content plus the original's own depth + 1).
   ========================================================================== */
.module-hub-content.module-hub-content {
  /* H1 — frame: no min-width, groups not cards */
  .module-hub-container { min-width: 0; padding: 12px 24px 24px; gap: 20px; }
  .module-body { gap: 20px; padding-bottom: 0; }

  /* H2 — "Add New Module": group label + one row of 40px hairline tiles */
  .add-new-module {
    padding: 0;
    gap: 6px;
    border: 0;
    border-radius: 0;
    background-color: transparent;
    .module-header .module-header-title {
      margin: 4px 0 0;
      font-size: 0.625rem;
      line-height: 16px;
      font-weight: 500;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--fts-form-helper);
    }
  }
  .module-items {
    gap: 8px;
    .module-item {
      min-width: 0;
      max-width: none;
      height: 40px;
      flex-direction: row;
      align-items: center;
      gap: 8px;
      padding: 0 12px;
      border: 1px solid var(--fts-gray-200);
      border-radius: var(--inistate-rounded-4);
      background-color: var(--inistate-color-shade-white);
      transition: background-color 0.15s ease;
      &:hover {
        background-color: var(--inistate-color-neutral-50);
        border-color: var(--fts-gray-200);
      }
      &.disable,
      &.disable:hover {
        background-color: transparent;
        border-color: var(--fts-gray-100);
        color: var(--fts-default);
      }
      .module-item-header { display: flex; align-items: center; gap: 6px; }
      .module-item-title { font-size: 16px; line-height: 20px; font-weight: 400; }
      .module-item-title-icon { height: 16px; width: 16px; }
      .module-item-title-icon-row { height: 20px; gap: 4px; }
      .module-item-soon {
        margin-left: 6px;
        padding: 0;
        border-radius: 0;
        background-color: transparent;
        color: var(--fts-default);
        font-size: 0.625rem;
        line-height: 16px;
        font-weight: 500;
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }
      .module-item-body { display: flex; align-items: baseline; gap: 8px; min-width: 0; }
      .module-item-sub {
        margin: 0;
        font-size: 0.8125rem;
        line-height: 20px;
        font-weight: 500;
        color: var(--fts-text);
        white-space: nowrap;
      }
      .module-item-description {
        margin: 0;
        font-size: 0.75rem;
        line-height: 16px;
        color: var(--inistate-text-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      &.disable .module-item-sub,
      &.disable .module-item-description { color: var(--fts-default); }
    }
  }

  /* H3 — module list: group label with the count as digits, 32px search, 36px rows */
  .list-modules {
    padding: 0;
    gap: 6px;
    border: 0;
    border-radius: 0;
    background-color: transparent;
    .list-header .list-title {
      gap: 8px;
      font-size: 0.625rem;
      line-height: 16px;
      font-weight: 500;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--fts-form-helper);
      .list-modules-count {
        padding: 0;
        border-radius: 0;
        background-color: transparent;
        color: var(--inistate-text-secondary);
        font-size: 0.75rem;
        font-weight: 400;
        letter-spacing: 0;
        text-transform: none;
        font-variant-numeric: tabular-nums;
      }
    }
  }
  .list-modules-inner { gap: 4px; }
  .list-search {
    .fts-form-control {
      width: 220px;
      height: 32px;
      min-height: 32px;
      padding: 0 30px 0 10px;
      font-size: 0.8125rem;
      border-color: var(--fts-gray-200);
    }
    .material-icons-outlined { font-size: 16px; color: var(--fts-default); }
  }
  .list-items {
    grid-template-columns: 1fr;
    gap: 0;
    .list-item {
      min-height: 36px;
      align-items: center;
      gap: 10px;
      padding: 0 4px 0 8px;
      border-radius: var(--inistate-rounded-4);
      border-bottom: 1px solid var(--fts-gray-100);
      background-color: transparent;
      box-shadow: none;
      &:hover {
        background-color: var(--inistate-color-neutral-50);
        .list-item-delete { opacity: 1; }
      }
      .list-item-icon {
        width: 28px;
        height: 28px;
        min-width: 28px;
        border-radius: 0;
        background-color: transparent;
        .list-item-emoji { font-size: 16px; }
      }
      .list-item-content { gap: 0; }
      .list-item-title {
        font-size: 0.8125rem;
        line-height: 20px;
        font-weight: 500;
        color: var(--fts-text);
      }
      .list-item-title-error { margin-top: 0; font-size: 0.75rem; line-height: 16px; }
      .list-item-action {
        gap: 2px;
        color: var(--fts-default);
        svg { width: 16px; height: 16px; }
      }
      .list-item-delete {
        opacity: 0;
        transition: opacity 0.15s ease, color 0.15s ease;
        &:hover { color: var(--fts-danger); }
      }
    }
  }

  /* H4 — empty: one line, no illustration (the block keeps its click-to-create) */
  .list-items-empty {
    height: auto;
    min-height: 36px;
    flex-direction: row;
    justify-content: flex-start;
    align-items: baseline;
    gap: 6px;
    padding: 8px 0;
    border-radius: 0;
    text-align: left;
    &:hover { background-color: transparent; }
    .list-items-empty-img { display: none; }
    .list-items-empty__title {
      margin: 0;
      font-size: 0.8125rem;
      line-height: 20px;
      font-weight: 400;
      color: var(--inistate-text-secondary);
      &::after { content: " —"; }
    }
    .list-items-empty__description {
      font-size: 0.8125rem;
      line-height: 20px;
      color: var(--inistate-text-secondary);
    }
  }
}

.module-hub-content {
  height: 100%;
}

.module-hub-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  gap: 2rem;
  padding: 1.5rem 2rem;
  min-width: 900px;
}

.module-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 1.5rem;
  height: 100%;
}

.add-new-module {
  border-radius: var(--inistate-rounded-8);
  border: 1px dashed rgba(var(--fts-primary-rgb), 0.5);
  background-color: var(--inistate-color-primary-50);
  padding: 1.5rem;
  gap: 1rem;
  display: flex;
  flex-direction: column;

  .module-header {
    .module-header-title {
      color: var(--inistate-text-primary-color);
      font-size: var(--inistate-heading-text-18);
      line-height: var(--inistate-heading-text-18--line-height);
      font-weight: var(--inistate-font-weight-bold);
    }
  }
}

.module-items {
  display: flex;
  gap: 1rem;
  align-items: stretch;

  .module-item {
    min-width: 150px;
    cursor: pointer;
    user-select: none;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    border-radius: var(--inistate-rounded-8);
    padding: 1rem;
    background-color: #ffffff;
    max-width: 254px;
    border: 1px solid transparent;

    &.default {
      background-color: #ffffff;
    }

    &.disable {
      cursor: default;
      background-color: var(--inistate-color-neutral-50);

      .module-item-body {

        .module-item-sub,
        .module-item-description {
          color: var(--inistate-text-tertiary);
        }
      }

      .module-item-title-icon {
        mix-blend-mode: color-burn;
      }
    }

    &.active {
      background-color: var(--inistate-color-primary-100);
      border: 1px solid var(--inistate-border-primary-active);
    }

    &:hover {
      background-color: var(--inistate-color-neutral-50);
      border-color: transparent;
    }

    .module-item-header {
      display: flex;
      justify-content: space-between;

      .module-item-title {
        font-weight: var(--inistate-font-weight-bold);
        font-size: var(--inistate-heading-text-24);
        line-height: var(--inistate-heading-text-24--line-height);
      }

      .module-item-title-icon {
        height: var(--inistate-heading-text-24--line-height);
      }

      .module-item-title-icon-row {
        display: flex;
        align-items: center;
        gap: 4px;
        height: var(--inistate-heading-text-24--line-height);

        .module-item-title-icon {
          height: 20px;
          width: 20px;
        }

      }

      .module-item-soon {
        display: flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        background-color: var(--inistate-color-primary-100);
        color: var(--inistate-border-info);
        margin-left: 0.5rem;
        font-weight: var(--inistate-font-weight-medium);
        font-size: var(--inistate-paragraph-text-12);
        line-height: var(--inistate-paragraph-text-12--line-height);
        border-radius: var(--inistate-rounded-20);
        padding: 2px 8px;
      }
    }

    .module-item-body {
      .module-item-sub {
        font-weight: var(--inistate-font-weight-medium);
        font-size: var(--inistate-heading-text-16);
        line-height: var(--inistate-heading-text-16--line-height);
        color: var(--inistate-text-primary-color);
        margin-bottom: 4px;
      }

      .module-item-description {
        font-size: var(--inistate-label-text-14);
        line-height: var(--inistate-label-text-14--line-height);
        color: var(--inistate-text-secondary-color);
      }
    }
  }
}

// List of Modules
.list-modules {
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 1rem;
  border-radius: var(--inistate-rounded-8);
  padding: 1.5rem;
  border: 1px solid var(--inistate-color-neutral-100);
  background-color: #ffffff;

  .list-header {
    .list-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: var(--inistate-font-weight-bold);
      font-size: var(--inistate-heading-text-18);
      line-height: var(--inistate-heading-text-18--line-height);

      .list-modules-count {
        // hide for current version
        display: none;
        // display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--inistate-color-neutral-100);
        color: var(--inistate-text-secondary-color);
        font-weight: var(--inistate-font-weight-medium);
        font-size: var(--inistate-paragraph-text-12);
        line-height: var(--inistate-paragraph-text-12--line-height);
        border-radius: var(--inistate-rounded-20);
        padding: 0.125rem 0.5rem;
      }
    }
  }

  .list-modules-inner {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 1rem;
  }

  .list-search {
    label {
      display: flex;
      align-items: center;
      position: relative;
      width: fit-content;

      input {
        width: 330px;
        border-radius: var(--inistate-rounded-4);
        padding-right: 2.25rem;
      }

      .material-icons-outlined {
        position: absolute;
        right: 0.75rem;
        font-size: 1rem;
        cursor: text;
      }

      .close-btn {
        cursor: pointer;
      }
    }
  }

  .list-items-empty {
    grid-column: 1 / -1;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    cursor: pointer;
    border-radius: var(--inistate-rounded-8);
    transition: background 0.2s;

    &:hover {
      background-color: rgba(var(--fts-primary-rgb), 0.05);
    }

    .list-items-empty-img {
      width: 162px;
      margin-bottom: 16px;
    }

    .list-items-empty__title {
      font-weight: var(--inistate-font-weight-medium);
      font-size: var(--inistate-heading-text-16);
      color: var(--inistate-text-primary-color);
      margin-bottom: 8px;
    }

    .list-items-empty__description {
      text-align: center;
      font-size: var(--inistate-label-text-14);
      color: var(--inistate-text-secondary-color);
      line-height: var(--inistate-label-text-14--line-height);
    }
  }

  .list-items {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;

    .list-item {
      gap: 12px;
      background-color: #ffffff;
      border-radius: var(--inistate-rounded-8);
      padding: 1rem;
      box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.1);
      display: flex;
      align-items: center;
      overflow: hidden;

      .list-item-icon {
        width: 60px;
        height: 60px;
        min-width: 60px;
        background-color: var(--inistate-color-primary-50);
        border-radius: var(--inistate-rounded-8);
        display: flex;
        align-items: center;
        justify-content: center;

        .list-item-emoji {
          font-size: var(--inistate-heading-text-24);
        }
      }

      .list-item-content {
        z-index: 2;
        display: flex;
        flex-direction: column;
        gap: 4px;
        overflow: hidden;

        .list-item-title {
          overflow: hidden;
          text-overflow: ellipsis;
          font-weight: var(--inistate-font-weight-bold);
          font-size: var(--inistate-heading-text-20);
          line-height: var(--inistate-heading-text-20--line-height);
        }

        .list-item-title-input {
          overflow: hidden;
          text-overflow: ellipsis;
          display: none;
        }

        .list-item-title-error {
          margin-top: 0.25rem;
          color: var(--fts-danger);
          font-size: var(--inistate-label-text-14);
          line-height: var(--inistate-label-text-14--line-height);
        }

        .list-item-sub {
          font-weight: var(--inistate-font-weight-light);
          font-size: var(--inistate-label-text-14);
          line-height: var(--inistate-label-text-14--line-height);
          color: var(--inistate-text-secondary-color);

          .list-item-state {
            display: none; // hiding for current version
            background-color: var(--inistate-icon-success);
            color: #ffffff;
            margin-left: 8px;
            font-weight: var(--inistate-font-weight-medium);
            font-size: var(--inistate-label-text-12);
            line-height: var(--inistate-label-text-12--line-height);
            border-radius: var(--inistate-rounded-4);
            padding: 2px 4px;

            &.draft {
              color: #ffffff;
              background-color: var(--inistate-color-yellow-700);
            }
          }
        }

        &.rename {
          .list-item-title-input {
            display: block;
            border-radius: var(--inistate-rounded-8);
            border: 1px solid var(--inistate-border-primary);
            padding: 10px 8px 10px 10px;
            font-size: var(--inistate-label-text-14);
            line-height: var(--inistate-label-text-14--line-height);

            &:hover {
              border-color: var(--inistate-border-primary-active);
            }
          }

          .list-item-title {
            display: none;
          }
        }
      }

      .list-item-action {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 1rem;
        color: var(--fts-gray-300);
        height: 100%;

        .list-item-delete {
          display: none;
          padding: 8px;
        }

        .list-item-nav {
          padding: 8px;
        }
      }

      &:hover {
        cursor: pointer;
        background-color: var(--fts-gray-50);

        .list-item-delete {
          display: block;
        }
      }
    }
  }
}


.ai-integration-modal {
  .ai-integration-modal__title {
    font-weight: var(--inistate-font-weight-bold);
    font-size: var(--inistate-heading-text-16);
    line-height: var(--inistate-heading-text-16--line-height);
    color: var(--inistate-text-primary-color);
  }

  .ai-integration-modal__desc {
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: var(--inistate-text-secondary-color);
  }

  .ai-integration-modal__content {
    padding: 24px;
    max-height: 652px;
    overflow-y: auto;
  }

  .ai-integration-modal__footer {
    padding: 24px;
    padding-top: 0;
  }
}

.ai-integration-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// Connect to ChatGPT banner card
.ai-gpt-connector {
  position: relative;
  display: flex;
  align-items: start;
  gap: 24px;
  padding: 24px;
  border-radius: var(--inistate-rounded-8);
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.1);
  background-color: var(--inistate-color-base-white, #ffffff);
  overflow: hidden;

  &__bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
    opacity: 0.2;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__logo {
    position: relative;
    z-index: 1;
    width: 68px;
    height: 68px;
    border-radius: 16px;
    flex-shrink: 0;
    object-fit: contain;
  }

  &__info {
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  &__title {
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-heading-text-24);
    line-height: var(--inistate-heading-text-24--line-height);
    color: var(--inistate-text-primary-color);
  }

  &__desc {
    font-weight: var(--inistate-font-weight-light);
    font-size: var(--inistate-paragraph-text-16);
    line-height: var(--inistate-paragraph-text-16--line-height);
    color: var(--inistate-text-secondary-color);
  }

  &__features {
    display: flex;
    gap: 16px;
    align-items: center;
    flex-wrap: wrap;
  }

  &__feature {
    display: flex;
    gap: 10px;
    align-items: center;
    font-weight: var(--inistate-font-weight-light);
    font-size: var(--inistate-paragraph-text-16);
    line-height: var(--inistate-paragraph-text-16--line-height);
    color: var(--inistate-text-secondary-color);
  }

  &__feature-icon {
    height: 20px;
    color: var(--inistate-icon-success);
  }

  &__btn.fts-btn-lite {
    padding: var(--inistate-spacing-8) var(--inistate-spacing-12);
    background: #fff;
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    white-space: nowrap;
  }
}


.ai-tutorials-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.ai-tutorials-provider-icons {
  display: flex;
  gap: 4px;
  align-items: center;
}

.ai-tutorials-provider-icon {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  object-fit: contain;
}

.ai-integration__api-key {
  border: 1px dashed rgba(var(--fts-primary-rgb), 0.5);
  border-radius: var(--inistate-rounded-8);
  padding: 24px;
  background-color: var(--inistate-color-primary-50);

  .ai-integration-label {
    font-weight: var(--inistate-font-weight-bold);
    font-size: var(--inistate-heading-text-16);
    line-height: var(--inistate-heading-text-16--line-height);
    margin-bottom: 8px;
  }

  .ai-integration-warning {
    font-weight: var(--inistate-font-weight-light);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    color: $warningColor;
  }

  .ai-integration-api-key-wrapper {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-top: 16px;
    padding: 3.5px 6px 3.5px 12px;
    border: 1px solid rgb(var(--fts-border-rgb));
    border-radius: var(--inistate-rounded-4);
    background-color: var(--inistate-color-neutral-50);

    .ai-integration-api-key {
      position: relative;
      flex: 1;
      font-weight: var(--inistate-font-weight-light);
      font-size: var(--inistate-label-text-14);
      line-height: var(--inistate-label-text-14--line-height);
      word-break: break-all;
    }

    .ai-integration-copy-btn {
      color: var(--inistate-text-secondary-color);
      border-radius: 9999px;
    }

    .ai-integration-copy-hint {
      @include absolute-center-y;
      right: 0;
      padding: 12px;
      box-shadow: var(--fts-elevation-3);
      border-radius: .25rem;
      background: var(--fts-popover-alt-background);
      color: var(--fts-text);
      font-size: var(--inistate-label-text-14);
    }
  }

  .ai-integration-generate-action {
    margin-top: 16px;
  }
}

.ai-integration-cards {
  display: flex;
  align-items: stretch;
  gap: 24px;
}

.ai-integration-card {
  flex: 1;
  min-width: 0;
  border-radius: var(--inistate-rounded-8);
  padding: 24px;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.1);
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
  transition: background-color 0.2s;

  &:hover {
    background: var(--inistate-color-neutral-50);
    text-decoration: none;
    color: inherit;
  }

  .ai-integration-card__icon {
    width: 68px;
    height: 68px;
    border-radius: var(--inistate-rounded-8);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .ai-integration-card__name {
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-heading-text-24);
    line-height: var(--inistate-paragraph-text-16--line-height);
    color: var(--inistate-text-primary-color);
    margin-top: 24px;
  }

  .ai-integration-card__desc {
    margin-top: 8px;
    font-weight: var(--inistate-font-weight-light);
    color: var(--inistate-text-secondary-color);
    font-size: var(--inistate-paragraph-text-16);
    line-height: var(--inistate-label-text-16--line-height, var(--inistate-paragraph-text-16--line-height));
    flex: 1;
  }

  .ai-integration-card__link {
    display: flex;
    align-items: center;
    gap: 0.5em;
    font-weight: var(--inistate-font-weight-medium);
    color: var(--inistate-text-primary-color);
    margin-top: 24px;

    .ai-integration-card__link_icon {
      display: inline-flex;
      align-items: center;

      img {
        width: 10.58px;
        height: 10.58px;
      }
    }
  }
}

.ai-integration-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--inistate-paragraph-text-14);
  font-weight: var(--inistate-font-weight-light);
  line-height: var(--inistate-paragraph-text-14--line-height);
  border-radius: var(--inistate-rounded-4);
  padding: 12px;
  width: 100%;
  color: var(--inistate-text-secondary-color);
  background-color: var(--inistate-color-neutral-50);

  .ai-integration-tip__accent {
    flex-shrink: 0;
    color: var(--inistate-text-primary-color);
    font-weight: var(--inistate-font-weight-medium);
  }
}
</style>
